/**
 * space_iff.ts — friend-or-foe COLOUR for space contacts (2026-09-08: "ships
 * on your side glow red at a certain distance and green when you get closer
 * — make friendly ships always glow green at any distance").
 *
 * The distance-scaled contact markers (SpaceModule) painted EVERY combatant
 * red beyond 220 m; inside it the marker faded and the hull's own halo
 * showed — green for a wingman. Pure: one rule for the marker AND the hull
 * halo, so a friend reads green at every range.
 */
export const IFF_FRIEND = 0x5cff9d;
export const IFF_HOSTILE = 0xff5555;
export const CIV_COLOR: readonly [number, number] = [0xffd873, 0x9fe8ff]; // aurelian gold, borean ice

export interface IffInputs {
  /** A wingman spawned on the player's side. */
  ally: boolean;
  /** The contact's war side (0 aurelian, 1 borean, -1 unaligned / den pirate). */
  side: 0 | 1 | -1;
  /** The player's sworn side (-1 unaligned). */
  mySide: 0 | 1 | -1;
}

/** Is this contact on the player's side? A wingman always; a war hull only
 *  when the player has sworn to its civ. Unaligned den pirates never. */
export function isFriendly(i: IffInputs): boolean {
  return i.ally || (i.mySide !== -1 && i.side === i.mySide);
}

/** The contact-marker colour at ANY range: green friend, red everything else. */
export function contactColor(i: IffInputs): number {
  return isFriendly(i) ? IFF_FRIEND : IFF_HOSTILE;
}

/** The hull's engine-halo colour up close: green for a friend; a hostile war
 *  hull keeps its civ colour (the enemy "glows red no matter what distance"
 *  through its marker; up close its livery says which civ), a den pirate its
 *  red-orange threat glow. */
export function haloColor(i: IffInputs, civFallback: number): number {
  return isFriendly(i) ? IFF_FRIEND : civFallback;
}

/** A lane wing's marker: your own civ's wings are friends; the other civ's
 *  wear their civ colour (they turn hostile only when they PROMOTE). */
export function wingMarkerColor(wingCiv: 'aurelian' | 'borean', myFaction: string): number {
  if (myFaction === wingCiv) return IFF_FRIEND;
  return wingCiv === 'aurelian' ? CIV_COLOR[0] : CIV_COLOR[1];
}

// ── THE GLYPH LAW (space campaign B, 2026-09-30; report §10.1: "side/hostility uses icon/text plus color") ──────
// Colour alone excludes a colour-blind pilot and a low-tier frame. Every contact class also has a WORD and a GLYPH,
// and a war hull's label names its side and class (the target line read "Deep space raider 1" for every war gunship,
// escort and patrol — `targetName` only knew den pirates).
export interface IffTag { glyph: '◆' | '▲'; word: 'friend' | 'hostile' }
export function iffTag(i: IffInputs): IffTag {
  return isFriendly(i) ? { glyph: '◆', word: 'friend' } : { glyph: '▲', word: 'hostile' };
}
export const SIDE_NAME: readonly [string, string] = ['Aurelian Concord', 'Borean Compact'];

/** A sided hull's class from its key (war gunship / escort / capital, a civ patrol, a lane wing), else null. */
export function sidedHullClass(key: string): string | null {
  if (/^war[01]:sk\d/.test(key)) return 'gunship';
  if (/^war[01]:fri\d/.test(key)) return 'frigate';
  if (/^war[01]:cor\d/.test(key)) return 'corvette';
  if (/^cap[01]:\d/.test(key)) return 'capital';
  if (/^patrol[01]/.test(key)) return 'patrol';
  return null;
}

/** "▲ Borean Compact frigate" / "◆ Aurelian Concord gunship (friend)" — null for an unsided contact. */
export function sidedHullLabel(key: string, i: IffInputs): string | null {
  const cls = sidedHullClass(key);
  if (!cls || (i.side !== 0 && i.side !== 1)) return null;
  const t = iffTag(i);
  return `${t.glyph} ${SIDE_NAME[i.side]} ${cls}${t.word === 'friend' ? ' (friend)' : ''}`;
}
